import { workspacePresentations, type WorkspaceId } from './workspace-presentation.js';
import type { AgentProposalDto } from './agent-setup.js';
import type { AdminTaskDto } from './admin-task.js';
import type { Id, IsoDate } from './common.js';
import type { DraftPatchProposal } from './draft-patch.js';
import type { PainelGrafico, PainelTabelaDados } from './painel-blocos.js';

/**
 * CONVERSA CENTRAL - o que a conversa de gestão devolve para a tela.
 *
 * A interface **nunca** renderiza markup vindo do modelo. Ela recebe um destes
 * objetos e escolhe o componente: o modelo escolhe a pergunta, o servidor
 * escolhe os números, a tela escolhe o desenho. Um cartão desconhecido não é
 * desenhado — é ignorado com aviso, e não interpretado como HTML.
 */
export const ADMIN_CARD_KINDS = [
  'metric',
  'comparison',
  'list',
  'feature_request',
  'catalog_proposal',
  'commerce_proposal',
  'catalog_results',
  'team_access',
  'screen_help',
  'draft_patch',
  'screen_fill',
  'grafico',
  'tabela',
] as const;
export type AdminCardKind = (typeof ADMIN_CARD_KINDS)[number];

/**
 * De onde o número veio, junto do número.
 *
 * Um indicador sem definição, período e instante não é verificável — e numa
 * tela de gestão isso vale menos que não mostrar. `definition` é a mesma frase
 * que a página de Resultados usa, e não uma explicação escrita pelo modelo.
 */
export interface AdminMetricCard {
  kind: 'metric';
  label: string;
  /** Valor formatado como a tela mostra: "R$ 3.000", "12", "R$ 1.000". */
  value: string;
  /** Valor cru, para comparação e teste. */
  raw: number;
  unit: 'currency' | 'count';
  /** "hoje", "últimos 7 dias" — no fuso da empresa. */
  period: string;
  timezone: string;
  definition: string;
  /** Quando a consulta foi feita. Um cartão antigo continua dizendo o que dizia. */
  queriedAt: IsoDate;
  /** Para onde a tela leva quem quiser ver os registros. */
  detail?: AdminDetailLink;
  /** Linhas auxiliares: "3 vendas", "ticket médio R$ 1.000". */
  extras?: { label: string; value: string }[];
}

export interface AdminComparisonCard {
  kind: 'comparison';
  label: string;
  current: { period: string; value: string; raw: number };
  previous: { period: string; value: string; raw: number };
  /**
   * Diferença percentual. `null` quando a base é zero — e aí a tela mostra a
   * diferença absoluta com "sem base para variação percentual", em vez de
   * infinito ou de um número que engana.
   */
  changePercent: number | null;
  changeAbsolute: string;
  definition: string;
  timezone: string;
  queriedAt: IsoDate;
}

/** Uma linha da lista: só o que a decisão exige. */
export interface AdminListRow {
  id: Id;
  title: string;
  /** Valor quando pertinente ao tipo da lista. */
  value?: string;
  /** Situação atual, em linguagem de negócio. */
  status?: string;
  /** Por que este registro entrou na lista. Nunca "porque sim". */
  reason?: string;
  /** Para onde a tela leva ao clicar. */
  href?: string;
  /** LANC-c: mensagem pronta para a pessoa abrir no WhatsApp dela (nada e enviado pelo Rimati). */
  whatsapp?: {
    /** appointment/birthday registram "aberto"; message (L2) e so o link, sem registro. */
    kind: 'appointment' | 'birthday' | 'message';
    message: string;
    /** Nulo quando o telefone falta ou e invalido. */
    link: string | null;
    problem: 'sem_telefone' | 'telefone_invalido' | null;
    openedAt?: string | null;
  };
}

export interface AdminListCard {
  kind: 'list';
  purpose?: 'capability-catalog' | 'business-template' | 'document-versions';
  document?: {
    companyId: string;
    userId: string;
    id: string;
    versions: { id: string; number: number; filename: string; canDownload: boolean }[];
  };
  /** Impedimentos visíveis mesmo quando os detalhes do modelo estão recolhidos. */
  blockers?: string[];
  label: string;
  /** Total de registros que satisfazem o filtro, e não o tamanho da página. */
  total: number;
  rows: AdminListRow[];
  /** O recorte usado, em palavras: "hoje · canal WhatsApp". */
  filters: string[];
  definition: string;
  queriedAt: IsoDate;
  detail?: AdminDetailLink;
}

/**
 * PN6: gráfico de um bloco de painel na conversa. `grafico` é exatamente o objeto que
 * `POST /business-dashboard/:id` devolve em `painel.graficos` para o mesmo período.
 * `detail` ("Abrir no painel") só existe com a ferramenta Painéis ativa.
 */
export interface AdminGraficoCard {
  kind: 'grafico';
  label: string;
  moduleId: string;
  grafico: PainelGrafico;
  /** Texto do período, no fuso da empresa: "01/10 a 29/10". */
  period: string;
  definition: string;
  queriedAt: IsoDate;
  detail?: AdminDetailLink;
}

/** PN6: tabela de um bloco de painel na conversa (`painel.tabelas` do mesmo período). */
export interface AdminTabelaCard {
  kind: 'tabela';
  label: string;
  moduleId: string;
  tabela: PainelTabelaDados;
  period: string;
  definition: string;
  queriedAt: IsoDate;
  detail?: AdminDetailLink;
}

export interface AdminDetailLink {
  label: string;
  href: string;
}

export interface AdminFeatureRequestCard {
  kind: 'feature_request';
  /** Opcional para manter cartões históricos legíveis. */
  catalog?: AdminListCard;
  operationId: string;
  companyId: string;
  userId: string;
  title: string;
  description: string;
}
export interface AdminCatalogProposalCard {
  kind: 'catalog_proposal';
  companyId: string;
  userId: string;
  operationId: string;
  tool:
    | 'catalogo.create'
    | 'catalogo.update'
    | 'catalogo.archive'
    | 'catalogo.price-batch-apply'
    | 'catalogo.import-apply';
  summary: string;
  input: unknown;
  createdAt: IsoDate;
}
/**
 * "Como faco" guiado pela tela (A3): passos do catalogo de ajuda do servidor.
 *
 * Os rotulos e a sequencia de alvos vem do catalogo, nunca do modelo. A pagina
 * encontra cada alvo por `data-rimati-target`; as dependencias (`openedBy`,
 * `dependsOn`) dizem o que apontar primeiro quando o alvo ainda nao existe.
 */
export interface AdminScreenHelpTarget {
  id: string;
  label: string;
  requires?: string[];
  openedBy?: string;
  /** Texto do balao quando este alvo e apontado por estar atras de outro (ex.: caixa fechado). */
  openHint?: string;
  dependsOn?: { target: string; value: string };
  unavailable?: true;
  managementOnly?: true;
}
export interface AdminScreenHelpCard {
  kind: 'screen_help';
  /** Chave de `SCREEN_MODULES`: os botoes "Mostrar" so valem nessa tela. */
  module: string;
  questionId: string;
  title: string;
  steps: Array<{ text: string; targets: string[]; unavailable?: true; managementOnly?: true }>;
  /** Os alvos citados pelos passos e os que os abrem (cadeia `openedBy`/`dependsOn`). */
  targets: AdminScreenHelpTarget[];
  /** "Fazer isso por mim": so quando o catalogo marca a acao como possivel. A execucao e a A4. */
  doForMe: { possible: boolean; via: 'draft' | 'review-card' | null };
}
/**
 * "FACA POR MIM" (A4): o Rimati preencheu o RASCUNHO da tela. O cartao leva a
 * proposta (patch ja validado e normalizado pelo servidor), que o painel entrega
 * a pagina. Nada e gravado: confirmar e o botao da propria tela.
 */
export interface AdminDraftPatchCard {
  kind: 'draft_patch';
  companyId: string;
  userId: string;
  /** Chave de `SCREEN_MODULES`: so vale na tela de onde saiu. */
  module: string;
  screen: string;
  proposal: DraftPatchProposal;
  /** O que muda, em linhas de pessoa (rotulo, de, para), escritas pelo servidor. */
  lines: Array<{ field: string; label: string; from: string; to: string }>;
  /** O botao nativo que a pessoa usa em seguida ("Revisar venda"). */
  confirmLabel: string;
  /**
   * Pedido grande ou improvavel (quantidade alta, troco exagerado, carrinho esvaziado):
   * o painel pergunta a PESSOA antes de entregar o preenchimento a tela. A confirmacao
   * e um clique no cartao, nunca um argumento que o modelo preenche.
   */
  requiresConfirmation?: string;
  /**
   * Valor do pagamento que a pessoa NAO disse: o campo fica vazio e o cartao oferece o botao
   * "Usar o total". `proposal` e o preenchimento do total, so entregue a tela no clique.
   */
  useTotal?: { totalCents: number; question: string; proposal: DraftPatchProposal };
  issuedAt: IsoDate;
  /** Depois disso o preenchimento expira e nao e mais entregue a pagina. */
  expiresAt: IsoDate;
}

/**
 * Cartao de revisao para telas SEM rascunho: mostra o que seria preenchido e o
 * botao "Aplicar na tela". O servidor nao grava nada; a tela preenche o
 * formulario e a pessoa confirma pelo botao dela.
 */
export interface AdminScreenFillCard {
  kind: 'screen_fill';
  companyId: string;
  userId: string;
  module: string;
  screen: string;
  /** Formulario da tela (`caixa.movimento`, `cliente.novo`...). */
  form: string;
  title: string;
  fields: Array<{ key: string; label: string; value: string | number; display: string }>;
  confirmLabel: string;
  issuedAt: IsoDate;
  expiresAt: IsoDate;
}
export interface AdminCatalogResultsCard {
  kind: 'catalog_results';
  companyId: string;
  rows: AdminListRow[];
  page: number;
  hasMore: boolean;
  filters: string[];
  queriedAt: IsoDate;
  definition: string;
}
function proposalToolsWithPrefix<Prefix extends string, Action extends string>(
  prefix: Prefix,
  actions: readonly Action[],
): `${Prefix}${Action}`[] {
  return actions.map((action) => `${prefix}${action}` as `${Prefix}${Action}`);
}

/**
 * Operações que podem chegar ao cartão de confirmação da conversa de gestão.
 *
 * Esta lista é compartilhada com a interface para que uma proposta emitida pelo
 * servidor nunca termine como "não reconhecida" apenas porque o catálogo visual
 * ficou desatualizado. As apresentações por ferramenta são acrescentadas logo
 * abaixo a partir do mesmo catálogo que define `WorkspaceId`.
 */
export const ADMIN_COMMERCE_PROPOSAL_STATIC_TOOLS = [
  'estoque.lot-enable',
  'estoque.lot-recall',
  'estoque.lot-recall-release',
  'estoque.recall-notice',
  'crm.radar-stage-policy',
  'crm.radar-next-step',
  'crm.pipeline-create',
  'crm.opportunity-create',
  'crm.opportunity-move-stage',
  ...proposalToolsWithPrefix('encomendas.', [
    'field-cancellation-cancel',
    'field-cancellation-refund',
    'field-extra-request',
    'field-my-extra-request',
    'field-my-transition',
    'field-my-delivery-incident',
    'field-extra-quote',
    'field-extra-decide',
    'field-delivery-incident',
    'field-delivery-policy',
    'field-delivery-book',
    'field-contract',
    'field-bill',
    'field-contract-cancel',
    'field-service',
    'field-technician',
    'field-schedule',
    'field-quick-os',
    'field-transition',
    'field-assign',
    'material-cost',
    'issue',
    'create',
    'quote-update',
    'accept',
    'art',
    'approve-art',
    'material',
    'adjust-material',
    'resource',
    'recipe',
    'queue',
    'transition',
    'partial-delivery',
    'close-balance',
    'pay',
    'cancel',
    'ready-notice-settings',
    'piece-entry',
    'piece-correct',
    'quote-auto-accept',
    'quote-link-revoke',
  ] as const),
  ...proposalToolsWithPrefix('posvenda.case-', [
    'exchange-simple',
    'exchange-paid',
    'exchange-refund',
    'return-exchange',
    'cost-recover',
    'open',
    'assign',
    'receive',
    'inspect',
    'approve',
    'request-refund',
    'withdraw-refund',
    'complete',
    'reopen',
    'close',
    'dispose',
    'my-return',
  ] as const),
  ...proposalToolsWithPrefix('posvenda.linked-', ['open', 'assign', 'resolve', 'close'] as const),
  ...proposalToolsWithPrefix('posvenda.intake-', [
    'manual-create',
    'pickup-authorize',
    'pickup-verify',
    'address-change-apply',
    'triage',
    'link-online-order',
    'resolve',
  ] as const),
  'posvenda.return',
  'posvenda.refund-settle',
  'contacts.create',
  'contacts.merge',
  'contacts.change',
  'followups.action',
  'crm.lost-demand',
  'agenda.bulk-cancel',
  'agenda.appointment-create',
  'agenda.appointment-reschedule',
  'agenda.appointment-status',
  'agenda.quick-setup',
  // L3a: paridade da saúde pela conversa (confirmação pela rota da tela).
  'clinical-record.draft',
  'clinical-record.meal-plan-share',
  'agenda.series-create',
  'contacts.measurement',
  'clinical-material.consume',
  ...proposalToolsWithPrefix('health-billing.', ['payer-plan-setup', 'plan-update', 'claim-create', 'authorization-register'] as const),
  ...proposalToolsWithPrefix('care-package.', ['sale', 'historical-import', 'manage', 'replacement-policy', 'absence', 'replacement-request'] as const),
  ...proposalToolsWithPrefix('no-show-fee.', ['policy', 'charge'] as const),
  ...proposalToolsWithPrefix('deposit.', ['policy', 'receive', 'waive', 'refund'] as const),
  // Entrega de venda do balcão: marcar para entregar, situação e prestação de contas do entregador.
  ...proposalToolsWithPrefix('entrega.', ['create', 'status', 'settle'] as const),
  ...proposalToolsWithPrefix('agenda.waitlist-', ['add', 'offer', 'accept', 'remove'] as const),
  // Lucro por trabalho: vincular despesa ou pagamento a parceiro a uma OS, encomenda ou projeto.
  'lucro.expense-link',
  // MEI: marcar o DAS como pago (e lançar a despesa paga, se houver valor), pela rota da tela.
  'mei.das-pay',
  ...proposalToolsWithPrefix('horas.', [
    'entry-create',
    'entry-update',
    'entry-adjust',
    'entry-reopen',
    'entry-delete-draft',
    'timer-start',
    'timer-stop',
    'entry-submit',
    'entry-return',
    'period-submit',
    'period-review',
    'period-approve',
    'rate-publish',
    'rate-replace',
    'billing-create',
    'billing-reverse',
    'expense-link',
    'prepare-person-access',
    'os-extra-request',
  ] as const),
  ...proposalToolsWithPrefix('equipe.', [
    'access-update',
    'professional-link',
    'invite',
    'invite-resend',
    'invite-revoke',
    'role-update',
    'status-update',
    'password-reset',
    'online-access-update',
  ] as const),
  ...proposalToolsWithPrefix('planejamento.', [
    'goal-create',
    'goal-update',
    'task-create',
    'task-update',
    'task-my-status',
  ] as const),
  ...proposalToolsWithPrefix('estoque.', [
    'count',
    'policy',
    'opening-count',
    'batch-adjust',
    'count-request',
    'count-review',
    'move',
    'transfer',
    'transfer-receive',
    'reservation-create',
    'reservation-release',
    'reservation-consume',
    'loss-request',
    'loss-review',
    'conditional-send',
    'conditional-return',
    'opening-order',
    'opening-order-resolve',
  ] as const),
  ...proposalToolsWithPrefix('financas.', [
    'move',
    'expense',
    'expense-amend',
    'expense-rename',
    'schedule',
    'generate',
    'schedule-and-generate',
    'recognize-customer-advance',
    'pay',
    'reverse-expense',
    'reverse-movement',
    'freeze-policy',
    'loan',
    'loan-pay',
    'policy',
    'policy-draft',
    'policy-draft-activate',
    'accounting-access',
    'fiscal-request',
  ] as const),
  ...proposalToolsWithPrefix('precos.', ['group-quote', 'group-review'] as const),
  ...proposalToolsWithPrefix('compras.', [
    'policy-set',
    'approve',
    'supplier',
    'supplier-update',
    'supplier-quote',
    'create',
    'reschedule',
    'receive',
    'return',
    'return-resolve',
    'cost-baseline',
  ] as const),
  ...proposalToolsWithPrefix('recebiveis.', [
    'credit-link',
    'credit-configure',
    'credit-approve',
    'dispute-open',
    'dispute-resolve',
    'collection-reminder',
    'settle',
    'fiado-receive',
    'reverse',
    'reschedule',
    'cancel',
  ] as const),
  ...proposalToolsWithPrefix('vendas.', [
    'complete-cost',
    'lost-record',
    'exception-request',
    'exception-decide',
    'offer-save',
    'offer-cancel',
    'finish',
    'conditional-convert',
    'cancel',
    'commission-rate',
  ] as const),
  // ML185l: venda da vendedora pelo balcão (caixa próprio).
  ...proposalToolsWithPrefix('balcao.', [
    'finish',
    'open',
    'close',
    // PG2: comandas do balcão e andamento na cozinha, pelos mesmos comandos da tela.
    'tab-open',
    'tab-add',
    'tab-close',
    'tab-cancel',
    'tab-kitchen-set',
  ] as const),
  ...proposalToolsWithPrefix('formularios.', [
    'create',
    'update',
    'publish',
    'pause',
    'archive',
    'collection-open',
    'collection-close',
    'submission-annotate',
    'submission-discard',
    'submission-reopen',
    'conversion-contact',
    'conversion-opportunity',
    'conversion-request',
  ] as const),
  ...proposalToolsWithPrefix('patrimonio.', [
    'asset-create',
    'asset-update',
    'asset-assign',
    'asset-receive',
    'asset-transfer',
    'asset-return',
    'asset-release',
    'asset-retire',
    'plan-create',
    'plan-update',
    'plan-pause',
    'order-generate',
    'failure-register',
    'order-start',
    'order-complete',
    'order-cancel',
    'meter-register',
    'meter-correct',
    'stock-consumption-prepare',
    'expense-prepare',
  ] as const),
  ...proposalToolsWithPrefix('locacao.', [
    'resource-configure',
    'quote-create',
    'booking-confirm',
    'booking-change',
    'booking-cancel',
    'handover-register',
    'account-default-set',
    'return-register',
    'inspection-complete',
    'extra-charge-prepare',
    'deposit-release-prepare',
  ] as const),
  ...proposalToolsWithPrefix('rotinas.', [
    'template-create',
    'template-update',
    'template-publish',
    'template-archive',
    'schedule-create',
    'schedule-pause',
    'schedule-update',
    'run-start',
    'run-answer',
    'run-complete',
    'run-reopen',
    'run-cancel',
    'exception-resolve',
  ] as const),
  ...proposalToolsWithPrefix('conciliacao.', [
    'change',
    'file-choices',
    'import-file',
    'record-balance',
    'cancel-file',
    'financial-adjustment',
  ] as const),
  ...proposalToolsWithPrefix('orcamento.', [
    'center-create',
    'center-update',
    'center-archive',
    'budget-create',
    'budget-revise',
    'budget-submit',
    'budget-approve',
    'budget-close',
    'rule-define',
    'allocation-propose',
    'allocation-apply',
    'allocation-reverse',
    'budget-transfer',
  ] as const),
  ...proposalToolsWithPrefix('reembolsos.', [
    'claim-create',
    'claim-update',
    'claim-submit',
    'claim-withdraw',
    'line-review',
    'policy-set',
  ] as const),
  ...proposalToolsWithPrefix('escalas.', [
    'plan-create',
    'shift-create',
    'shift-update',
    'shift-remove-draft',
    'shift-cancel',
    'coverage-set',
    'availability-set',
    'schedule-publish',
    'schedule-revise',
    'swap-request',
    'swap-approve',
    'swap-reject',
    'swap-cancel',
    'absence-register',
    'absence-cancel',
  ] as const),
  ...proposalToolsWithPrefix('fidelidade.', [
    'program-create',
    'version-publish',
    'program-pause',
    'membership-enroll',
    'membership-leave',
    'earn',
    'reverse',
    'redemption-prepare',
    'redemption-confirm',
    'redemption-cancel',
    'adjustment',
  ] as const),
  'contas.create',
  'contas.create-starter',
  'contas.create-category',
  'contas.archive',
  'contas.archive-category',
  'contas.opening-balance',
  'contas.opening-balance-reverse',
  ...proposalToolsWithPrefix('caixa.', [
    'sales-participants',
    'open',
    'close',
    'movement',
    'reopen',
    'payout',
    'payout-destination',
    'late-credit',
    'monthly-regularization',
  ] as const),
  'indicadores.save',
  ...proposalToolsWithPrefix('catalogo.presentation-', ['save', 'restore'] as const),
  'catalogo.consignment-settle',
  'catalogo.consignment-set',
  // Receita do prato: ficha, margem mínima de alerta e esgotado do dia.
  ...proposalToolsWithPrefix('catalogo.', ['recipe-save', 'recipe-remove', 'dish-margin', 'sold-out'] as const),
  ...proposalToolsWithPrefix('loja.', ['save', 'publish-product', 'preview', 'enable'] as const),
  ...proposalToolsWithPrefix('pedidos-online.', [
    'reserve',
    'cancel',
    'expire',
    'payment-create',
    'payment-simulate',
    'return-request',
    'return-receive',
    'return-assess',
    'refund-simulate',
    'payment-retry',
    'payment-cancel-authorization',
    'payment-register-restitution',
    'payment-integration-control',
    'fulfill',
    'manual-create',
    'manual-update',
    'manual-payment',
    'manual-cancel',
  ] as const),
  'publicacao.change',
  ...proposalToolsWithPrefix('campanhas.', ['draft', 'schedule', 'cancel'] as const),
  ...proposalToolsWithPrefix('projetos.', [
    'commercial-link',
    'commercial-link-cancel',
    'create',
    'update-draft',
    'baseline',
    'start',
    'pause',
    'resume',
    'cancel',
    'close',
    'milestone-start',
    'milestone-reopen',
    'delivery-register',
    'delivery-decide',
    'scope-change-propose',
    'scope-change-approve',
    'scope-change-cancel',
    'task-update',
  ] as const),
  ...proposalToolsWithPrefix('documentos.', [
    'upload-prepare',
    'upload-complete',
    'metadata-update',
    'version-publish',
    'archive',
    'renewal-register',
    'reminder-config',
    'share-create',
    'share-revoke',
  ] as const),
  ...proposalToolsWithPrefix('conhecimento.', ['create', 'update', 'state'] as const),
  ...proposalToolsWithPrefix('assistentes-comerciais.', ['save', 'activate-test'] as const),
  ...proposalToolsWithPrefix('atendente-vitrine.', [
    'bind',
    'close-handoff',
    'respond-handoff',
  ] as const),
] as const;

export type AdminCommerceProposalTool =
  (typeof ADMIN_COMMERCE_PROPOSAL_STATIC_TOOLS)[number] | `${WorkspaceId}.ui-${'save' | 'restore'}`;

export const ONLINE_ORDER_ACTION_LABELS: Record<string, string> = {
  prepare: 'Começar a separar o pedido',
  ready: 'Marcar como pronto para retirada',
  ship: 'Registrar envio do pedido',
  'ship-partial': 'Registrar envio de parte do pedido',
  'update-shipment': 'Atualizar acompanhamento do envio',
  deliver: 'Confirmar recebimento pela cliente ou pelo cliente',
  'deliver-shipment': 'Confirmar recebimento de um volume',
  'request-cancellation': 'Solicitar cancelamento do pedido',
};

/** Cartões que a conversa privada "Meu trabalho" pode mostrar: a tarefa própria e as ações do próprio trabalho (PG4). */
export const OWN_WORK_CHAT_PROPOSAL_TOOLS: readonly string[] = [
  'planejamento.task-my-status',
  'encomendas.field-my-transition',
  'encomendas.field-my-extra-request',
  'encomendas.field-my-delivery-incident',
  'posvenda.case-my-return',
];

export const ADMIN_COMMERCE_PROPOSAL_TOOLS: readonly AdminCommerceProposalTool[] = [
  ...ADMIN_COMMERCE_PROPOSAL_STATIC_TOOLS,
  ...Object.keys(workspacePresentations).flatMap(
    (id) => [`${id}.ui-save`, `${id}.ui-restore`] as AdminCommerceProposalTool[],
  ),
];

export interface AdminCommerceProposalCard {
  kind: 'commerce_proposal';
  companyId: string;
  userId: string;
  operationId: string;
  tool: AdminCommerceProposalTool;
  input: unknown;
  summary: string;
  createdAt: IsoDate;
}
export interface AdminModuleSetupCard {
  kind: 'module_setup';
  companyId: string;
  userId: string;
  moduleId: string;
  name: string;
  settings?: Record<string, string | number | boolean>;
  /** Informações recebidas para retomar o cadastro; não representam produto salvo. */
  pendingProduct?: { name: string; priceCents?: number; colors?: string[]; sizes?: string[] };
  /** Validated draft input to resume after human preparation, never a write authorization. */
  continuation?: { tool: string; input: unknown };
}
export interface AdminTeamAccessCard {
  kind: 'team_access';
  companyId: string;
  userId: string;
  name: string;
  email: string;
  role: 'sales' | 'inventory';
}
export type AdminCard =
  | AdminDraftPatchCard
  | AdminScreenFillCard
  | AdminScreenHelpCard
  | AdminTeamAccessCard
  | AdminModuleSetupCard
  | AdminCommerceProposalCard
  | AdminMetricCard
  | AdminComparisonCard
  | AdminListCard
  | AdminFeatureRequestCard
  | AdminCatalogProposalCard
  | AdminCatalogResultsCard
  | AdminGraficoCard
  | AdminTabelaCard;

/**
 * O recorte ativo da conversa (seção 16 do escopo).
 *
 * Vive na conversa, é visível e substituível. "E os do WhatsApp?" acrescenta
 * canal ao recorte; "agora este mês" troca o período. Sem isso, cada pergunta
 * recomeçaria do zero e a continuidade seria só aparência.
 */
export const ADMIN_PERIODS = ['today', 'yesterday', '7d', '30d', '90d', 'month', 'week'] as const;
export type AdminPeriod = (typeof ADMIN_PERIODS)[number];

export const ADMIN_PERIOD_LABELS: Record<AdminPeriod, string> = {
  today: 'hoje',
  yesterday: 'ontem',
  '7d': 'últimos 7 dias',
  '30d': 'últimos 30 dias',
  '90d': 'últimos 90 dias',
  month: 'este mês',
  week: 'esta semana',
};

export interface AdminChatFilters {
  period: AdminPeriod;
  /**
   * CANAL DE ATENDIMENTO — e não origem de aquisição.
   *
   * O produto registra por onde a conversa aconteceu (`conversations.channelId`),
   * e não como o cliente chegou até a clínica. Alguém que veio por indicação e
   * foi atendido pelo WhatsApp tem identidade de WhatsApp. Chamar isso de
   * "origem" prometeria uma atribuição que nenhum campo sustenta.
   */
  channel?: 'whatsapp' | 'simulator' | null;
}

/**
 * O contexto de página que a conversa carrega (seção 15.3).
 *
 * Guardado por ID e tipo, nunca por texto: ao agir, o serviço recarrega o
 * registro. "Aquele contato" reconstruído a partir do que o modelo escreveu é
 * como uma seleção aprovada vira outra seleção.
 */
export const ADMIN_CONTEXT_KINDS = ['contact', 'opportunity', 'appointment'] as const;
export type AdminContextKind = (typeof ADMIN_CONTEXT_KINDS)[number];

export interface AdminChatContextDto {
  kind: AdminContextKind;
  recordId: Id;
  label: string;
}

export interface AdminChatSessionDto {
  id: Id;
  continuationSessionId?: Id;
  title: string;
  lastActivityAt: IsoDate;
  archived: boolean;
  filters: AdminChatFilters;
  context: AdminChatContextDto | null;
}

/**
 * O objetivo de trabalho que atravessa conversas administrativas.
 *
 * A jornada pertence à empresa E à pessoa. Ela não é uma lista de mensagens:
 * guarda somente o plano de continuidade e evidências confirmadas. Dados de
 * entrada de operações, valores sensíveis e raciocínio do modelo não entram
 * neste contrato.
 */
export const ADMIN_JOURNEY_STATUSES = ['active', 'paused', 'completed', 'cancelled'] as const;
export type AdminJourneyStatus = (typeof ADMIN_JOURNEY_STATUSES)[number];

export const ADMIN_JOURNEY_STEP_KINDS = ['product', 'stock', 'cash', 'sale', 'general'] as const;
export type AdminJourneyStepKind = (typeof ADMIN_JOURNEY_STEP_KINDS)[number];

export const ADMIN_JOURNEY_STEP_STATUSES = [
  'pending',
  'awaiting_confirmation',
  'completed',
  'deferred',
] as const;
export type AdminJourneyStepStatus = (typeof ADMIN_JOURNEY_STEP_STATUSES)[number];

export interface AdminJourneyPendingOperationDto {
  operationId: string;
  tool: string;
  summary: string;
  /** Conversa que contém o cartão original, para não preparar outro igual. */
  sessionId: Id;
  messageId: Id;
}

export interface AdminJourneyStepDto {
  key: string;
  title: string;
  kind: AdminJourneyStepKind;
  status: AdminJourneyStepStatus;
  dependsOn: string[];
  deferReason: string | null;
  pendingOperation: AdminJourneyPendingOperationDto | null;
  completedAt: IsoDate | null;
}

export interface AdminJourneyResultDto {
  stepKeys: string[];
  text: string;
  operationId: string;
  confirmedAt: IsoDate;
}

export interface AdminJourneyDto {
  id: Id;
  objective: string;
  status: AdminJourneyStatus;
  steps: AdminJourneyStepDto[];
  /** Primeira etapa liberada pelas dependências, ou null quando está pausada/concluída. */
  nextStepKey: string | null;
  lastResult: AdminJourneyResultDto | null;
  sourceSessionId: Id;
  lastSessionId: Id;
  createdAt: IsoDate;
  updatedAt: IsoDate;
}

export interface AdminChatMessageDto {
  id: Id;
  /**
   * `system` e o marco da conversa ("Agora em: PDV"): gravado pelo servidor, nunca
   * enviado ao modelo como fala nem exibido como mensagem de ninguem.
   */
  role: 'user' | 'assistant' | 'system';
  /** Quando `role` e `system`: o tipo do marco. */
  kind?: 'screen_change';
  text: string;
  authorName: string;
  createdAt: IsoDate;
  cards: AdminCard[];
  /**
   * Preenchido quando a consulta falhou.
   *
   * "Não foi possível consultar" e "zero" são estados distintos, e a diferença
   * precisa sobreviver ao recarregamento — senão a releitura de um erro vira um
   * relatório de que não houve vendas.
   */
  unavailable: string | null;
  /**
   * R011 - por onde conseguir a mesma resposta sem a IA.
   *
   * Só aparece quando a consulta falhou, e é o que separa "não consegui" de
   * "você está sem saída": as telas continuam de pé, respondem a mesma
   * pergunta por consulta determinística, e a pessoa não tem por que saber
   * disso no momento em que a conversa para de funcionar.
   *
   * Derivado do recorte e do contexto da conversa, no servidor. Guardado junto
   * da mensagem pelo mesmo motivo de `unavailable`: ao reabrir, a saída
   * precisa continuar lá.
   */
  alternatives: AdminChatLink[];
}

export interface AdminChatLink {
  label: string;
  href: string;
}

/**
 * O que a tela da conversa recebe.
 *
 * As propostas vêm ao LADO das mensagens, e não dentro delas: uma proposta muda
 * de estado quando alguém decide, e a mensagem que a trouxe não muda nunca. A
 * tela junta as duas pela `messageId` da proposta.
 */
export interface AdminChatViewDto {
  session: AdminChatSessionDto;
  messages: AdminChatMessageDto[];
  /** Só as pendentes: as já decididas viram estado do registro, não da conversa. */
  proposals: AgentProposalDto[];
  /**
   * As tarefas duráveis desta conversa, com o estado ATUAL de cada envio.
   *
   * Diferente das propostas, elas NÃO somem depois de decididas: "como ficou
   * aquela ação de ontem?" precisa encontrar a execução, e uma tarefa que
   * desaparece ao terminar não tem como ser consultada.
   */
  tasks: AdminTaskDto[];
  /** Objetivo atual desta pessoa, inclusive quando começou em outra conversa. */
  journey: AdminJourneyDto | null;
}

export interface AdminChatReplyDto {
  session: AdminChatSessionDto;
  /** A mensagem enviada e a resposta, nesta ordem. */
  messages: AdminChatMessageDto[];
  /** As alterações propostas por esta resposta, todas pendentes. */
  proposals: AgentProposalDto[];
  /** O plano preparado por esta resposta, quando houver. */
  tasks: AdminTaskDto[];
  /** Estado depois desta rodada; nunca é reconstruído do texto da resposta. */
  journey: AdminJourneyDto | null;
}
